import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import GenericSelect from '../../components/ui/GenericSelect';
import { PageWrap, FloatingFilters, FilterIcon, Grid } from './styles';
import { useExpenseDashboardApi } from './config/useExpenseDashboardApi';
import { buildCategoryColorMap, buildProjectColorMap } from './constants';
import KpiRow from './components/KpiRow';
import CategoryTrendsCard from './components/CategoryTrendsCard';
import RecentTransactionsCard from './components/RecentTransactionsCard';
import EntryHeatmapCard from './components/EntryHeatmapCard';
import TopSpendersCard from './components/TopSpendersCard';
import MonthPaceCard from './components/MonthPaceCard';
import DeptCategoryCard from './components/DeptCategoryCard';
import LocationDistributionCard from './components/LocationDistributionCard';
import ApprovalPipelineCard from './components/ApprovalPipelineCard';
import RiskSignalsCard from './components/RiskSignalsCard';
import BudgetUsageCard from './components/BudgetUsageCard';
import MonthComparisonCard from './components/MonthComparisonCard';
import SixMonthTrendCard from './components/SixMonthTrendCard';
import ProjectShareCard from './components/ProjectShareCard';
import ProjectTrendCard from './components/ProjectTrendCard';
import ProjectBudgetCard from './components/ProjectBudgetCard';
import ProjectCategoryMixCard from './components/ProjectCategoryMixCard';

export default function ExpenseDashboardPage() {
  const { t } = useTranslation();
  const {
    filters, setFilters, slices, departments, locationOptions, categories, refetchAll,
  } = useExpenseDashboardApi();

  const categoryColors = useMemo(() => buildCategoryColorMap(categories), [categories]);
  // Proje renkleri — halka, trend ve bütçe kartlarında aynı proje aynı renkte.
  const projectColors = useMemo(() => buildProjectColorMap(slices.projects.data?.items), [slices.projects.data]);
  const filtersActive = Boolean(filters.departmentId || filters.locationId);

  return (
    <PageWrap>
      <FloatingFilters data-export-exclude>
        <FilterIcon><SlidersHorizontal size={14} /></FilterIcon>
        <GenericSelect
          options={departments}
          value={filters.departmentId}
          onChange={(value) => setFilters((prev) => ({ ...prev, departmentId: value || null }))}
          placeholder={t('expenseDashboard.filters.allDepartments')}
          size="small"
          direction="top"
        />
        <GenericSelect
          options={locationOptions}
          value={filters.locationId}
          onChange={(value) => setFilters((prev) => ({ ...prev, locationId: value || null }))}
          placeholder={t('expenseDashboard.filters.allLocations')}
          size="small"
          direction="top"
        />
      </FloatingFilters>
      <Grid>
        <KpiRow summary={slices.summary} risks={slices.risks} />
        <CategoryTrendsCard slice={slices.categories} categoryColors={categoryColors} />
        <RecentTransactionsCard slice={slices.recent} />
        <EntryHeatmapCard slice={slices.heatmap} />
        <TopSpendersCard slice={slices.topSpenders} />
        <MonthPaceCard slice={slices.summary} />
        <DeptCategoryCard slice={slices.deptCategory} categoryColors={categoryColors} />
        <LocationDistributionCard slice={slices.locations} />
        <ApprovalPipelineCard slice={slices.pipeline} onApproved={refetchAll} />
        <RiskSignalsCard slice={slices.risks} />
        <BudgetUsageCard slice={slices.budget} />
        <MonthComparisonCard slice={slices.summary} />
        <SixMonthTrendCard slice={slices.categories} />
        <ProjectShareCard slice={slices.projects} projectColors={projectColors} />
        <ProjectTrendCard slice={slices.projects} projectColors={projectColors} />
        <ProjectBudgetCard slice={slices.projects} filtersActive={filtersActive} />
        <ProjectCategoryMixCard slice={slices.projects} categoryColors={categoryColors} />
      </Grid>
    </PageWrap>
  );
}
